import type { PriceInfo } from "../../../api/pna/PriceInfo";
import type {
  EnhancedPriceData,
  NetPriceData,
  PriceData,
  TotalPriceData,
  WithSurcharge,
} from "../../../models/PriceData";
import { selectPriceScalingElement, selectRebate, selectSurcharge } from "../../../utils/pricePicker";
import {
  type FifteenDigitEntry,
  isKITKITProduct,
  type PackagingInformationEntry,
  type ProductData,
  ProductMarketType,
} from "../../../models/ProductData";
import { uniqueValues } from "../../../utils/misc";
import { CURRENCY_ISO, INVALID_PACKAGE_CODE } from "../../../utils/constants";
import type { LoadableEntry } from "../../../redux/loadableStore/types";
import type { AvailabilityInfo } from "../../../api/pna/AvailabilityInfo";
import type { RootState } from "../../../redux/store";
import { selectProductDescriptor } from "../../../redux/products/productsSelectors";
import { selectProperAmountInCart } from "../../../AddToCart/redux/cartAmounts/cartAmountSelectors";
import { multiply, volatileSum, zeroNetPrice, zeroTotalPrice } from "../../../utils/PriceUtils";
import type { LineItem } from "~/feature/cart/cart/models/CartData";
import type { LineItemBuilder } from "../../../utils/cartUpdating/LineItemUpdate";
import { selectPackageInStockAmount } from "../../../utils/pna/packagingInformationEntrySelectors";
import { isSubscription as isSubscriptionProduct } from "../../../utils/productUtils";
import { getVisibleNetPrice } from "~/feature/cart/cart/redux/selectors/productEntrySelectors";
import { canSeeListPrice, isSurchargeIncludedInPriceValue } from "~/utils/features";
import { hasInStock } from "~/utils/packageEntryUtils";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { isNonNull } from "~/utils/reducers";

interface GetPriceInfoProps {
  product: ProductDescriptor;
  packCode?: string;
}

export const getPriceInfo = ({ product, packCode }: GetPriceInfoProps): PriceInfo | null => {
  if (product.isMultiPrice) {
    return product.prices.filter((price) => price.packCode === packCode)[0] ?? null;
  } else {
    return product.prices[0] ?? null;
  }
};

interface SelectPriceProps {
  totalQuantity: number;
  product: ProductDescriptor;
  packCode: string;
}

type DataFromPriceInfoProps = Pick<SelectPriceProps, "product" | "packCode">;

export const getNetPrice = (props: SelectPriceProps): NetPriceData => {
  if (isKITKITProduct(props.product.base)) {
    // KIT products have no value of their own.
    return {
      value: 0,
      currencyIso: CURRENCY_ISO,
      isSubscription: isSubscription(props.product),
      isSpecial: false, // can't determine
      unit: 1,
      rebate: "",
      surcharge: 0,
      baseValue: 0,
      tariffSurcharge: false,
    };
  }

  const priceInfo = getPriceInfo(props) ?? undefined;
  const pricing = selectPriceScalingElement(props.totalQuantity, priceInfo?.priceScale)?.price;

  if (!pricing || !priceInfo) {
    return zeroNetPrice(props.product);
  }

  const surcharge = selectSurcharge(props.totalQuantity, priceInfo);

  const listPriceVisible = canSeeListPrice({
    product: props.product,
    isSpecialPrice: priceInfo.isSpecialPrice,
    hasSurcharge: surcharge > 0,
  });

  const rebate = listPriceVisible ? selectRebate(props.totalQuantity, priceInfo) : undefined;
  const tariffSurcharge = props.product.prices[0].isTariffSurchargeApplicable;
  return {
    ...pricing,
    isSubscription: isSubscription(props.product),
    isSpecial: priceInfo.isSpecialPrice,
    unit: priceInfo.priceUnit,
    rebate,
    surcharge,
    tariffSurcharge,
    baseValue: pricing.value,
    value: isSurchargeIncludedInPriceValue() ? pricing.value : pricing.value * (1 + surcharge),
  };
};

export const isSubscription = (props: ProductDescriptor) => {
  return isSubscriptionProduct(props.base);
};

export const getSurcharge = (props: SelectPriceProps): number => {
  if (isKITKITProduct(props.product.base)) {
    // KIT products have no value of their own, thus no surcharge
    return 0;
  }

  const priceInfo = getPriceInfo(props) ?? undefined;
  return selectSurcharge(props.totalQuantity, priceInfo) ?? 0;
};

export const getTariff = (product: ProductDescriptor) => {
  return product.prices[0]?.isTariffSurchargeApplicable;
};

export const getRebatePercentage = (props: SelectPriceProps): string | null => {
  const priceInfo = getPriceInfo(props);
  let rebateApplied = null;
  if (priceInfo && priceInfo.rebateScale.length > 0) {
    const sortedScales = [...priceInfo.rebateScale].sort((a, b) => (a.minQty > b.minQty ? 1 : -1));
    if (props.totalQuantity < 1) {
      rebateApplied = sortedScales[0].rebate;
    } else {
      sortedScales.forEach((scale: { minQty: number; rebate?: string }) => {
        if (props.totalQuantity >= scale.minQty) {
          rebateApplied = scale.rebate;
        }
      });
    }
  }
  return isRebateValid(props) && rebateApplied ? rebateApplied : null;
};

export const isRebateValid = (props: SelectPriceProps): boolean => {
  const listPrice = getListPrice({ packCode: props.packCode, product: props.product });
  const netPrice = getNetPrice(props);
  return listPrice !== null && listPrice.value > 0 && netPrice !== null && netPrice.value > 0;
};

export interface SelectNetPriceProps {
  extraQuantity?: number;
  productCode: string;
  packCode: string;
  priceReference: string;
}
export const selectNetPrice = (props: SelectNetPriceProps) => (state: RootState) => {
  const product = selectProductDescriptor(state, props);
  const cartQty = selectProperAmountInCart(props)(state);
  const totalQuantity = cartQty + (props.extraQuantity ?? 0);

  return getNetPrice({ totalQuantity, product, packCode: props.packCode });
};
export const selectNullableNetPrice = (props: SelectNetPriceProps) => (state: RootState) => {
  if (!props.productCode) {
    return null;
  }
  const product = selectProductDescriptor(state, props);
  if (!product) {
    return null;
  }
  return selectNetPrice(props)(state);
};

export const getListPrice = (props: DataFromPriceInfoProps): EnhancedPriceData | null => {
  const priceInfo = getPriceInfo(props) ?? undefined;

  const pricing = priceInfo?.listPrice;

  if (!pricing || !priceInfo) {
    return null;
  }

  return {
    ...pricing,
    // isSubscription: !props.product.base.quantityVisible,
    // List price doesn't care if you're a subscription
    isSubscription: false,
    unit: priceInfo.priceUnit,
  };
};

export function getQtyInStock(product: ProductDescriptor, packCode: string) {
  return selectPackageInStockAmount(getPackageByCode(product, packCode));
}

function getPackageByCode(product: ProductDescriptor, packCode: string) {
  return product.availability?.packagingInformation.filter((packaging) => packaging.packCode === packCode)[0] ?? null;
}

export const getPackagingInformationByCode = getPackageByCode;

export function getPackageByIndex(product: ProductDescriptor, packageIndex: number) {
  return product.availability?.packagingInformation[packageIndex] ?? null;
}

interface ProductCountries {
  product: ProductData;
  packCode?: string;
}
export function getCountriesList({ product, packCode }: ProductCountries): string[] {
  if (!product.fifteenDigit) {
    return [];
  }

  let countries: string[] = [];
  // if packCode exists, keeps the only ones that are matching
  if (packCode !== null && packCode !== undefined && packCode.trim().length > 0) {
    const packageData: FifteenDigitEntry = product.fifteenDigit[packCode];
    if (packageData?.countryOfOrigin) {
      countries = [packageData.countryOfOrigin];
    }
  } else {
    // no package data, grab all
    countries =
      uniqueValues(
        Object.values(product.fifteenDigit)
          .map((digit) => digit.countryOfOrigin)
          .flat()
          .filter(isNonNull),
      ) || [];
  }

  return countries;
}

interface ProductDescriptorCountries {
  descriptor: ProductDescriptor;
  packCode?: string;
}

export function getCountriesListFromFifteendigit({ descriptor, packCode }: ProductDescriptorCountries): string[] {
  return getCountriesList({ product: descriptor.base, packCode });
}

export function isProductPMA(descriptor: ProductDescriptor) {
  return (
    descriptor.base.isPMA ||
    descriptor.base.marketType === ProductMarketType.PMA ||
    (descriptor.availability?.packagingInformation?.length === 1 &&
      descriptor.availability.packagingInformation[0].packCode === INVALID_PACKAGE_CODE)
  );
}

/**
 * SCHAEFF-25688
 * On checkout page, when splitting between orderables and quotables, we do not consider DMA with XX pack code to be PMA
 * @param descriptor
 */
export function isProductPMAOnCheckout(descriptor: ProductDescriptor) {
  return descriptor.base.isPMA || descriptor.base.marketType === ProductMarketType.PMA;
}

export function makeDescriptorFromData(
  base: ProductData,
  price: PriceInfo[],
  availability: AvailabilityInfo,
  priceReference: string,
): ProductDescriptor {
  return {
    base,
    priceReference,
    isPriceLoading: false,
    isPriceError: false,
    prices: price,
    isMultiPrice: price.length > 1,
    isAvailabilityLoading: false,
    isAvailabilityError: false,
    availability,
    isLoaded: true,
    isError: false,
    isLoading: false,
  };
}

type fetchOptions = { fetchAvailability?: boolean };

export function makeDescriptorFromFullState(
  base: ProductData,
  priceDescriptor: LoadableEntry<PriceInfo[]> | undefined,
  availabilityDescriptor: LoadableEntry<AvailabilityInfo> | undefined,
  priceReference: string,
): ProductDescriptor {
  const isPriceError = priceDescriptor ? priceDescriptor.state === "error" : false;
  const isAvailabilityError = availabilityDescriptor ? availabilityDescriptor.state === "error" : false;
  const price = priceDescriptor && priceDescriptor.data;
  const availability = availabilityDescriptor && availabilityDescriptor.data;
  const isPriceLoading = priceDescriptor ? priceDescriptor.state === "loading" : false;
  const isAvailabilityLoading = availabilityDescriptor ? availabilityDescriptor.state === "loading" : false;
  return {
    base,
    priceReference,
    isPriceLoading,
    isPriceError,
    prices: price || [],
    isMultiPrice: price ? price.length > 1 : undefined,
    isAvailabilityLoading,
    isAvailabilityError,
    availability,
    isLoaded: !!price && !!availability && !!base,
    isError: isPriceError || isAvailabilityError,
    isLoading: isPriceLoading || isAvailabilityLoading,
  };
}

export function fakeDescriptorFromProduct(base: ProductData, priceReference?: string): ProductDescriptor {
  const isPriceError = false;
  const isAvailabilityError = false;
  const isPriceLoading = false;
  const isAvailabilityLoading = false;
  return {
    base,
    priceReference: priceReference ?? "",
    isPriceLoading,
    isPriceError,
    prices: [],
    isMultiPrice: undefined,
    isAvailabilityLoading,
    isAvailabilityError,
    isLoaded: false,
    isError: false,
    isLoading: false,
  };
}

export function displayBetterDeal(data: DataFromPriceInfoProps, totalQty: number): boolean {
  const priceInfo = getPriceInfo(data);
  const minQtyAvailableScale = priceInfo?.minQtyAvailableScale ?? null;
  if (minQtyAvailableScale === null) {
    return false;
  }
  return totalQty >= minQtyAvailableScale;
}

export const selectTotalPrice = (priceProps: SelectNetPriceProps, quantity: number) => (state: RootState) => {
  const netPrice = selectNetPrice(priceProps)(state);
  return getTotalPrice(netPrice, quantity);
};

export function getTotalPrice(price: NetPriceData | null | undefined, quantity: number): TotalPriceData & WithSurcharge;

export function getTotalPrice(
  price: NetPriceData | null | undefined,
  quantity: number,
): TotalPriceData & WithSurcharge {
  if (!price) {
    return zeroTotalPrice(false);
  }

  const { unit, baseValue, isSubscription, surcharge, currencyIso, tariffSurcharge } = price;

  let baseTotal = (baseValue * quantity) / unit;
  if (isSubscription) {
    baseTotal = baseTotal * 12;
  }

  return {
    currencyIso,
    isSubscription,
    surcharge,
    tariffSurcharge,
    value: baseTotal * (1 + surcharge),
    baseValue: baseTotal,
  };
}

export function getTotalPriceForLineItem(
  lineItem: LineItem,
  product: ProductData | ProductDescriptor,
): TotalPriceData & WithSurcharge;
export function getTotalPriceForLineItem(lineItem: LineItem, isSubscription: boolean): TotalPriceData & WithSurcharge;

export function getTotalPriceForLineItem(
  lineItem: LineItem,
  productOrSubscription: ProductData | ProductDescriptor | boolean,
): PriceData {
  const visiblePrice = getVisibleNetPrice(lineItem, productOrSubscription) ?? zeroNetPrice(productOrSubscription);
  return getTotalPrice(visiblePrice, lineItem.quantity);
}

export function getPricePerPiece(
  lineItem: LineItem | LineItemBuilder | NetPriceData | undefined,
): PriceData | undefined {
  if (!lineItem) {
    return undefined;
  }

  let netPrice: NetPriceData | undefined;
  if ("unit" in lineItem) {
    netPrice = lineItem;
  } else if ("get" in lineItem) {
    netPrice = lineItem.get("netPrice");
  } else {
    netPrice = lineItem.netPrice;
  }

  const unit = netPrice?.unit ?? 1;

  if (!netPrice || unit === 1) {
    return netPrice;
  }

  return multiply(netPrice, 1 / unit);
}

export const selectSubtotalPrice =
  (pricePropList: SelectNetPriceProps[]) =>
  (state: RootState): PriceData => {
    const prices = pricePropList
      // Filter out a KIT parent product, because it inherently cannot have a price
      .filter((priceProps) => {
        const product = selectProductDescriptor(state, priceProps);
        return !isKITKITProduct(product);
      })
      .map((priceProps) => selectTotalPrice(priceProps, priceProps.extraQuantity ?? 0)(state));

    return volatileSum(...prices);
  };

export function getSurchargeValueFromTotalPrice(totalPrice: TotalPriceData & WithSurcharge): PriceData {
  if (!totalPrice.surcharge) {
    return {
      currencyIso: totalPrice.currencyIso,
      value: 0,
    };
  }
  return {
    currencyIso: totalPrice.currencyIso,
    value: totalPrice.value - totalPrice.baseValue,
  };
}

export function productHasInStock(product: ProductDescriptor) {
  const availability = product.availability;
  return availability?.packagingInformation.some(hasInStock) ?? false;
}

export function getPackagingInformation(product: ProductDescriptor, packCode: string) {
  return product.availability?.packagingInformation.find((item) => item.packCode === packCode);
}

export function getPackageCodes(
  product: ProductDescriptor,
  filter?: (packaging: PackagingInformationEntry) => boolean,
) {
  let packages = product.availability?.packagingInformation ?? [];
  if (filter) {
    packages = packages.filter(filter);
  }
  return packages.map((packaging) => packaging.packCode);
}

export function resolveSmallestPackageFromProductData(product: ProductDescriptor) {
  const pack = Object.values(product.base.fifteenDigit).reduce(
    (acc, data) => {
      if (!acc) {
        return data;
      }
      return data.packSize < acc.packSize ? data : acc;
    },
    null as FifteenDigitEntry | null,
  );
  // last two digits
  return pack?.materialNo.substring(pack.materialNo.length - 2) ?? INVALID_PACKAGE_CODE;
}

export function resolvePackageSizeFromProductData(product: ProductDescriptor, packCode: string) {
  const pack = product.base.fifteenDigit[packCode];

  return pack?.packSize ?? 1;
}
